<script>
    import { onMount } from "svelte";
    // Tutorial: https://www.npmjs.com/package/amazon-cognito-identity-js
    // Passwords: https://github.com/nrao57/tutorials/blob/master/AWS-Cognito-Tutorials/Forgot%20Password/login.html
    // Hint from: https://github.com/aws/aws-sdk-js/issues/1769
    import AmazonCognitoIdentity from "amazon-cognito-identity-js/dist/amazon-cognito-identity.js";
    import { createUserPool, constructCognitoUser, getUserWpnId, getUserAttributes, getUserEmail } from "./scripts/awsUtils.js";
    import { createEventDispatcher } from "svelte";
    
    const dispatch = createEventDispatcher();
    export let wpnID;

    const apiUser = process.env.VITE_API_USER;
    const apiPW = process.env.VITE_API_PW;
    const apiDemo = process.env.VITE_API_DEMO
    let userPool
    let verCodeNewPasswordRequested = false;
    let willShowAdmin = false;
    let signInMode = "modeSignIn"; // SignIn | ChangePassword | | ForgotPassword | Admin
    let txtSignInEmail;
    let txtSignInPassword;
    let txtNewPWEmail
    let txtNewPassword;
    let txtNewPasswordConfirmed;
    let txtNewPasswordVerificationCode;
    let txtWpnId = null;
    let cognitoUser;

    onMount(() => {
        userPool = createUserPool();
        const optionStr = window.location.search;
        willShowAdmin = optionStr.includes('admin');
        console.log("Options: ", optionStr);
    });

    // Action: Signing in
    function willSignIn() {
        const email = txtSignInEmail.value;
        const password = txtSignInPassword.value;
        performSignIn(email, password);
    }

    function performSignIn(email, password) {
        cognitoUser = constructCognitoUser(email);
        var authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
            {
                Username: cognitoUser.username,
                Password: password,
            }
        );
        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: result => {
                try {
                    console.log("Success signing in: " + result);
                    signUserIn();
                } catch (error) {
                    alert("An error has occurred, please check internet connection and try again.");
                    console.log(error);
                }
            },
            onFailure: err => {
                alert("Error signing in: " + err.message);
            },
            newPasswordRequired: function(userAttributes, requiredAttributes) {
                console.log("New password required");
                delete userAttributes.email_verified;
                delete userAttributes.phone_number_verified;
                signInMode = "modeNewPasswordRequired";
            }
        });
    }

    // Forced to change password
    function willChangePasswordForced() {
        if (checkEnteredPassword() == false) {
            return;
        }
        const newPassword = txtNewPassword.value;
        var userAttributes = {};
        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
            onSuccess: (result) => {
                console.log('Password changed successfully:', result);
                signInMode = "modeSignIn";
            },
            onFailure: (err) => {
                alert(err.message || JSON.stringify(err));
            },
        });
    }

    // Action: Forgot password
    function willChangePasswordForgot() {
        if (checkEnteredPassword() == false) {
            return;
        }
        if (cognitoUser == undefined) {
            const email = txtNewPWEmail.value;
            cognitoUser = constructCognitoUser(email);
        }
        cognitoUser.forgotPassword({
            onSuccess: data => {
                // 1. Successfully initiated reset password request
                console.log('CodeDeliveryData from forgotPassword' + JSON.stringify(data))
                console.log("Changing password for registered user: ", result.user);
                alert("Requested new password.");
                verCodeNewPasswordRequested = true;
            },
            onFailure: err => {
                // 2. Failed to change password
                alert(err.message || JSON.stringify(err));
            },
            inputVerificationCode: data => {
                // 3. Callback when verification code has been sent
                console.log('Code sent to: ' + data);
                alert("Verification code sent: Please check your email inbox (or spam folder).");
                verCodeNewPasswordRequested = true;
            }
        });
    }

    function willVerifyNewPassword() {
        var verCode = txtNewPasswordVerificationCode.value;
        var newPassword = txtNewPassword.value;
        cognitoUser.confirmPassword(verCode, newPassword, {
            onSuccess() {
				console.log('Password change confirmed!');
                alert("Password changed successfully.");
                verCodeNewPasswordRequested = false;
                signInMode = "modeSignIn";
			},
			onFailure(err) {
				console.log('Error: Password change was not successful');
                verCodeNewPasswordRequested = false;
                alert(err.message || JSON.stringify(err));
			}
        });
    }

    // 5. Action: Exploring the demo utility
    function willExploreDemo() {
        console.log("Starting up demo");
        wpnID = apiDemo;
        performSignIn(apiUser, apiPW);
    }

    // 6. Action: Set or clear WPN ID
    function setWpnID() {
        wpnID = txtWpnId.value;
        signInMode = "modeSignIn";
    }

    function clearWpnID() {
        wpnID = null;
        signInMode = "modeSignIn";
    }

    function checkEnteredPassword() {
        const pw1 = txtNewPassword.value;
        const pw2 = txtNewPasswordConfirmed.value;
        const result = (pw1 == pw2)
        if (result == false) {
            alert("Error: New password and confirmation do not match.");
        }
        return result;
    }

    async function signUserIn() {
        const attributes = await getUserAttributes(cognitoUser);
        const loginEmail = getUserEmail(attributes);
        if (wpnID == undefined) {
            const userWPNID = getUserWpnId(attributes);
            wpnID = (userWPNID) ? userWPNID : apiDemo;
        }
        dispatch("signed-in", { wpnID: wpnID, loginEmail: loginEmail });
    }

</script>

<div class="sign-in-box">
    <div class="logo-panel">
        <div id="logo-image-panel">
            <img
                id="logo-image"
                src="images/iconALFXLogo.png"
                alt="WPN2 logo"
            />
        </div>
        <div id="logo-title-panel">
            <h2>ALFX Web Dashboard</h2>
        </div>
    </div>
    {#if signInMode == "modeForgotPassword"}
        <form class="forgot-password-panel">
            <fieldset>
                <label for="npEmail">Email</label>
                <input
                    class="input-item"
                    type="email"
                    name="npEmail"
                    bind:this={txtNewPWEmail}
                    autocomplete="on"
                />
                <label for="npPassword">New Password</label>
                <input
                    class="input-item"
                    type="password"
                    name="npPassword"
                    bind:this={txtNewPassword}
                    autocomplete="new-password"
                />
                <div class="create-password-hints">Password must have at least 8 letters, numbers & symbols</div>
                <label for="npConfirmPassword">Confirm</label>
                <input
                    class="input-item"
                    type="password"
                    name="npConfirmPassword"
                    bind:this={txtNewPasswordConfirmed}
                    autocomplete="new-password"
                />
            </fieldset>
            <div class="mode-button-panel">
                <button
                    class="other-action"
                    on:click={() => (signInMode = "modeSignIn")}
                    type="button"
                    >Cancel
                </button>
                <button
                    id="forgot-password-button"
                    class="action-button"
                    on:click={willChangePasswordForgot}
                    type="button"
                    >Submit Password
                </button>
            </div>
            {#if (verCodeNewPasswordRequested == true)}
                <fieldset>
                    <label for="npVerCode">Verification Code</label>
                    <input
                        class="short-input-item"
                        type="text"
                        name="npVerCode"
                        bind:this={txtNewPasswordVerificationCode}
                        pattern=".*"
                        required
                    />
                </fieldset>
                <div class="mode-button-panel">
                    <button
                        id="verification-new-password-button"
                        class="action-button"
                        on:click={willVerifyNewPassword}
                        type="button"
                    >Submit Code
                    </button>
                </div>
            {/if}
        </form>
    {:else if signInMode == "modeNewPasswordRequired"}
        <form class="new-password-panel">
            <fieldset>
                <label for="npEmail">Email</label>
                <input
                    class="input-item"
                    type="email"
                    name="npEmail"
                    bind:this={txtNewPWEmail}
                    autocomplete="on"
                />
                <label for="npPassword">New Password</label>
                <input
                    class="input-item"
                    type="password"
                    name="npPassword"
                    bind:this={txtNewPassword}
                    autocomplete="new-password"
                />
                <div class="create-password-hints">Password must have at least 8 letters, numbers & symbols</div>
                <label for="npConfirmPassword">Confirm</label>
                <input
                    class="input-item"
                    type="password"
                    name="npConfirmPassword"
                    bind:this={txtNewPasswordConfirmed}
                    autocomplete="new-password"
                />
            </fieldset>
            <div class="mode-button-panel">
                <button
                    class="other-action"
                    on:click={() => (signInMode = "modeSignIn")}
                    type="button"
                    >Cancel
                </button>
                <button
                    id="forgot-password-button"
                    class="action-button"
                    on:click={willChangePasswordForced}
                    type="button"
                    >Submit Password
                </button>
            </div>
        </form>
    {:else if signInMode == "modeAdmin"}
        <form class="alfx-id-panel">
            <fieldset>
                <label for="wpnId">ALFX Subscription ID</label>
                <input
                    class="input-item"
                    id="wpn-id"
                    type="text"
                    name="wpnId"
                    bind:this={txtWpnId}
                />
            </fieldset>
            <div class="mode-button-panel">
                <button
                    class="other-action"
                    on:click={clearWpnID}
                    type="button"
                    >Cancel
                </button>
                <button
                    id="wpn-id-button"
                    class="action-button"
                    on:click={setWpnID}
                    type="button"
                    >Submit ALFX ID
                </button>
            </div>
    </form>
    {:else if signInMode == "modeSignIn"}
        <form class="sign-in-panel">
            <fieldset>
                <div class="">
                    <label for="signInEmail">Email</label>
                    <input
                        class="input-item"
                        id="sign-in-email"
                        type="email"
                        name="signInEmail"
                        bind:this={txtSignInEmail}
                        autocomplete="on"
                    />
                </div>
                <div>
                    <label for="signInPassword">Password</label>
                    <input
                        class="input-item"
                        type="password"
                        name="signInPassword"
                        bind:this={txtSignInPassword}
                        autocomplete="on"
                    />
                </div>
                <div id="forgot-password-panel">
                    <a id="forgot-password" href={"#"} on:click={() => (signInMode = "modeForgotPassword")}
                        >Forgot password</a
                    >
                </div>
            </fieldset>
            <button
                id="sign-in-button"
                class="action-button"
                on:click={willSignIn}
                type="button"
                >Sign In
            </button>
            <div id="sign-in-divider" />
        </form>
        <div id="divider-panel" />
        <div class="other-actions-panel">
            <div class="admin-button-panel">
                {#if willShowAdmin}
                    <a class="active-link" id="admin-link" href={"#"} on:click={() => { signInMode = "modeAdmin" }}
                    >Admin</a>
                {/if}
            </div>
            <div class="explore-demo-button-panel">
                <button
                    class="other-action"
                    on:click={willExploreDemo}
                    type="button"
                    id="explore-demo-button"
                    >Or Explore Demo
                </button>
            </div>
        </div>
    {/if}
</div>

<style>
    .sign-in-box {
        max-width: 512px;
        margin: 32px auto auto auto;
        border-radius: 16px;
        background-color: ghostwhite;
    }
    .logo-panel {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        margin: auto;
        border-bottom: 1px solid rgb(67, 148, 11);
    }
    #logo-image-panel {
        width: 72px;
        height: 100%;
    }
    #logo-title-panel {
        height: 100%;
    }
    #logo-image {
        width: 72px;
        height: 72px;
    }
    .sign-in-panel {
        padding-top: 16px;
        text-align: center;
    }
    .forgot-password-panel {
        padding-top: 16px;
        text-align: right;
    }
    .new-password-panel {
        padding-top: 16px;
    }
    .alfx-id-panel {
        padding-top: 16px;
    }
    .mode-button-panel {
        text-align: center;
        padding-bottom: 16px;
    }
    #sign-in-email, #wpn-id {
        margin-bottom: 32px;
    }
    input[type="email"]:focus {
        border-left: 4px solid rgb(67, 148, 11);
        outline: none;
    }
    input[type="password"]:focus-visible {
        border-left: 4px solid rgb(67, 148, 11);
        outline: none;
    }
    fieldset {
        padding: 10px 0;
        border: none;
        width: 64%;
        text-align: center;
        margin: auto;
    }
    .input-item {
        width: 100%;
        margin: 0
    }
    .short-input-item {
        width: 100%;
        margin: 0;
    }
    #forgot-password {
        color: gray;
        font-size: 0.9rem;
    }
    #admin-link {
        color: blue;
        width: 64%;
        text-align: center;
        margin-left: 0.5rem;
    }
    #verification-new-password-button {
        margin: 16px 0;
    }
    #sign-in-button {
        display: block;
        margin: 16px auto;
    }
    #forgot-password-panel {
        text-align: right;
    }
    #divider-panel {
        height: 32px;
        width: 50%;
        margin: auto;
        border-bottom: 1px solid lightgray;
    }
    .other-actions-panel {
        color: gray;
        padding: 16px;
        text-align: center;
        width: 64%;
        margin: auto;
    }
    .admin-button-panel {
        text-align: center;
        margin-bottom: 16px;
        font-size: 0.9rem;
    }
    .create-password-hints {
        text-align: center;
        margin-bottom: 16px;
        color: gray;
        font-size: 0.7rem;
    }
    .explore-demo-button-panel {
        text-align: center;
    }
    #explore-demo-button {
        text-align: center;
        padding-bottom: 8px;
    }
    .other-action {
        margin: 8px 16px;
        border-radius: 5px;
        cursor: pointer;
        padding: 0.5rem 1rem;
        background-color: white;
    }
    .other-action:hover {
        border: 1px solid rgb(67, 148, 11);
    }
    .action-button {
        background-color: rgb(67, 148, 11);
        color: white;
        border-radius: 5px;
        cursor: pointer;
        padding: 0.5rem 1rem;
        border: 1px solid rgb(67, 148, 11);
    }
    .action-button:hover {
        background-color: rgb(75, 164, 14);
        border: 1px solid #0a6ebd;
    }
</style>
